import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Top Bar Banner (site-wide promotional strip)
export const TopBarBanner = () => {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/banners/active?location=topbar`);
        if (res.data && res.data.length > 0) {
          setBanner(res.data[0]);
        }
      } catch (err) {
        // Silent fail
      }
    };
    
    // Check if dismissed in session
    const isDismissed = sessionStorage.getItem('topbar_banner_dismissed');
    if (!isDismissed) {
      fetchBanner();
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('topbar_banner_dismissed', 'true');
  };

  if (!banner || dismissed) return null;

  return (
    <div 
      className="relative py-2 px-4 text-center text-sm font-medium"
      style={{ backgroundColor: banner.background_color, color: banner.background_color === '#CE8946' ? '#0a0203' : '#ffffff' }}
      data-testid="topbar-banner"
    >
      <div className="container mx-auto flex items-center justify-center gap-2">
        <span className="font-semibold">{banner.title}:</span>
        <span>{banner.text}</span>
        {banner.cta_link && (
          <Link 
            to={banner.cta_link} 
            className="ml-2 underline hover:no-underline flex items-center gap-1"
          >
            {banner.cta_text} <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      <button 
        onClick={handleDismiss}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:opacity-70 transition-opacity"
        aria-label="Dismiss banner"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Hero Banner (for homepage)
export const HeroBanner = ({ className = '' }) => {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/banners/active?location=homepage`);
        if (res.data && res.data.length > 0) {
          setBanner(res.data[0]);
        }
      } catch (err) {
        // Silent fail
      }
    };
    fetchBanner();
  }, []);

  if (!banner) return null;

  return (
    <div 
      className={`rounded-2xl p-8 md:p-12 text-white ${className}`}
      style={{ backgroundColor: banner.background_color }}
      data-testid="hero-banner"
    >
      <div className="max-w-2xl">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">{banner.title}</h2>
        <p className="text-lg md:text-xl opacity-90 mb-6">{banner.text}</p>
        {banner.cta_link && (
          <Link 
            to={banner.cta_link}
            className="inline-flex items-center gap-2 bg-white text-[#0a0203] px-6 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors"
          >
            {banner.cta_text}
            <ChevronRight className="h-5 w-5" />
          </Link>
        )}
      </div>
    </div>
  );
};

// Product Page Banner (inline promotional)
export const ProductBanner = ({ className = '' }) => {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/banners/active?location=products`);
        if (res.data && res.data.length > 0) {
          setBanner(res.data[0]);
        }
      } catch (err) {
        // Silent fail
      }
    };
    fetchBanner();
  }, []);

  if (!banner) return null;

  return (
    <div 
      className={`rounded-xl p-6 text-white flex flex-col md:flex-row items-center justify-between gap-4 ${className}`}
      style={{ backgroundColor: banner.background_color }}
      data-testid="product-banner"
    >
      <div>
        <h3 className="text-xl font-bold">{banner.title}</h3>
        <p className="opacity-90">{banner.text}</p>
      </div>
      {banner.cta_link && (
        <Link 
          to={banner.cta_link}
          className="whitespace-nowrap bg-white/20 hover:bg-white/30 px-5 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          {banner.cta_text}
          <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
};

// Generic Banner Component (fetches all active for location)
export const CampaignBanner = ({ location = 'all', variant = 'inline' }) => {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/banners/active${location !== 'all' ? `?location=${location}` : ''}`);
        setBanners(res.data || []);
      } catch (err) {
        // Silent fail
      }
    };
    fetchBanners();
  }, [location]);

  if (banners.length === 0) return null;

  if (variant === 'topbar') {
    return <TopBarBanner />;
  }

  return (
    <div className="space-y-4">
      {banners.map(banner => (
        <div 
          key={banner.id}
          className="rounded-xl p-6 text-white"
          style={{ backgroundColor: banner.background_color }}
        >
          <h3 className="text-xl font-bold mb-2">{banner.title}</h3>
          <p className="opacity-90 mb-4">{banner.text}</p>
          {banner.cta_link && (
            <Link 
              to={banner.cta_link}
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              {banner.cta_text}
              <ChevronRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

export default CampaignBanner;
